import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { of, Subscription, tap, filter, catchError, switchMap } from 'rxjs';

import { AuthUser } from '@shared/signup/models';
import { isOnFreePlan } from '@shared/user/functions';
import { ServiceSettings } from '@shared/restaurant/models';
import { Schedule } from '@shared/schedule/models';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { ServicesListComponent } from './components/services-list.component';
import {
  WhoService,
  ScheduleService,
  PricePlansRestrictionsService,
  PricePlansRestrictionsModalService,
} from '../../../services';

@Component({
  selector: 'tablesready-services',
  templateUrl: './services.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServicesComponent implements OnInit, OnDestroy {
  @ViewChild('serviceList') servicesListCmp: ServicesListComponent;

  icon = AppIcon;
  color = AppIconColor;
  size = AppIconSize;

  who: AuthUser;
  serviceSettings: ServiceSettings;
  availableSchedules: Schedule[] = [];
  sort: string[] = ['order', 'name', 'category'];
  isUserOnFreePlan = false;

  servicesForm: UntypedFormGroup;

  private availableForBusinessPlan = false;
  private subscriptions = new Subscription();

  constructor(
    private readonly alertsService: AlertsService,
    private readonly whoService: WhoService,
    private readonly fb: UntypedFormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly scheduleService: ScheduleService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly pricePlansRestrictionsModalService: PricePlansRestrictionsModalService,
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(
      this.authRestaurantService.select()
        .pipe(
          filter(user => user.location !== undefined),
          tap(response => {
            this.who = response;
            this.serviceSettings = {...response.location.serviceSettings};
            this.servicesForm = this.fb.group({
              enabled: [this.serviceSettings.enabled || false],
              label: [this.serviceSettings.label || '', Validators.required],
              showOnPages: [this.serviceSettings.showOnPages || false],
              sort: [this.serviceSettings.sort || 'order'],
              allowCharge: [this.serviceSettings.allowCharge || false, [Validators.required]],
            });
            this.availableForBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(response.account.subscriptionType, response.location.recurly);

            const enabledSub = this.servicesForm.get('enabled').valueChanges
              .pipe(
                filter((value: boolean) => value && !this.availableForBusinessPlan),
                switchMap(() => this.pricePlansRestrictionsModalService.display(this.who)),
                tap(() => {
                  this.servicesForm.get('enabled').setValue(false, { emitEvent: false });
                  this.cdr.detectChanges();
                }),
              ).subscribe();
            this.subscriptions.add(enabledSub);

            this.isUserOnFreePlan = isOnFreePlan(this.who);
            this.getSchedule();
            this.cdr.detectChanges();
          }),
        ).subscribe(),
    );
  }

  addEditService(): void {
    this.servicesListCmp.addEditService();
  }

  getSchedule() {
    this.subscriptions.add(
      this.scheduleService.selectAll()
        .pipe(
          tap(schedules => {
            this.availableSchedules = schedules;
            this.cdr.detectChanges();
          }),
          catchError(() => {
            this.alertsService.addError('Please configure business hours.');

            return of(null);
          }),
        )
        .subscribe(),
    );
  }

  saveServicesSettings(data) {
    this.servicesForm.markAsPristine();
    if(!data.sort) { delete data.sort; }
    this.serviceSettings = {...data};
    this.whoService.updateServiceSettings(data).subscribe(
      () => {
        this.alertsService.add({
          type: IAlertTypes.success,
          heading: 'Updated:',
          body: 'Services Settings',
        });
      },
      (error) => {
        this.alertsService.add({
          type: IAlertTypes.danger,
          heading: 'Error:',
          body: JSON.stringify(error),
        });
      }
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
